﻿package com.mgacreative.hotelru.feature.reservation.ui.checkin

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotelru.core.network.SupabaseNetworkClient
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

import com.mgacreative.hotelru.domain.repository.ReservationRepository
import com.mgacreative.hotelru.domain.repository.RoomRepository
import com.mgacreative.hotelru.domain.repository.PricingRepository
import com.mgacreative.hotelru.domain.repository.BlacklistRepository
import com.mgacreative.hotelru.domain.model.BlacklistedGuest

data class AvailableRoomUI(
    val id: String,
    val roomNumber: String,
    val roomType: String,
    val status: String,
    val currentPrice: Double
)

class CheckInViewModel(
    private val reservationRepository: ReservationRepository,
    private val roomRepository: RoomRepository,
    private val pricingRepository: PricingRepository,
    private val blacklistRepository: BlacklistRepository
) : ViewModel() {
    private val _isProcessing = MutableStateFlow(false)
    val isProcessing: StateFlow<Boolean> = _isProcessing.asStateFlow()

    private val _checkIns = MutableStateFlow<List<CheckInItemData>>(emptyList())
    val checkIns: StateFlow<List<CheckInItemData>> = _checkIns.asStateFlow()

    suspend fun checkBlacklist(fullName: String, identityNo: String): BlacklistedGuest? {
        return blacklistRepository.checkIsBlacklisted(fullName, identityNo)
    }

    fun loadTodayCheckIns() {
        viewModelScope.launch {
            try {
                val allReservations = reservationRepository.getReservations()
                // Durumu CONFIRMED olanları (veya CHECKED_IN olup odası atanmamışları) Check-In için göster
                val pendingList = allReservations.filter { 
                    it.status == "CONFIRMED" || (it.status == "CHECKED_IN" && it.roomId == null)
                }
                
                _checkIns.value = pendingList.map { res ->
                    val fName = res.guests?.firstName ?: ""
                    val lName = res.guests?.lastName ?: ""
                    CheckInItemData(
                        id = res.id ?: "",
                        guestName = if (fName.isNotBlank()) "$fName $lName" else "Guest",
                        firstName = fName,
                        lastName = lName,
                        identityNo = res.guests?.idPassportNo ?: "",
                        birthDate = res.guests?.birthDate ?: "",
                        rooms = listOf(res.rooms?.roomNumber ?: res.roomId ?: res.roomType ?: "Unknown"),
                        dateRange = "${res.checkInDate} - ${res.checkOutDate}",
                        checkInDate = res.checkInDate,
                        checkOutDate = res.checkOutDate,
                        statusText = "Pending",
                        isCheckInComplete = false,
                        roomType = res.roomType ?: ""
                    )
                }
            } catch (e: Exception) {
                // Handle error
            }
        }
    }

    suspend fun getAvailableRooms(roomType: String): List<AvailableRoomUI> {
        return try {
            val allRooms = roomRepository.getRooms()
            val availableRooms = allRooms.filter { 
                it.status.equals("AVAILABLE", ignoreCase = true) || it.status.equals("CLEAN", ignoreCase = true) 
            }
            
            val prices = pricingRepository.getAllPricePeriods()
            val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date.toString()
            
            availableRooms.map { room ->
                val activePrice = prices.filter { 
                    it.roomType.equals(room.roomType, ignoreCase = true) && it.startDate <= today && it.endDate >= today 
                }.maxByOrNull { it.price }?.price ?: 0.0
                
                AvailableRoomUI(
                    id = room.id ?: "",
                    roomNumber = room.roomNumber,
                    roomType = room.roomType,
                    status = room.status,
                    currentPrice = activePrice
                )
            }.sortedWith(compareByDescending<AvailableRoomUI> { it.roomType.equals(roomType, ignoreCase = true) }
             .thenBy { it.roomNumber.toIntOrNull() ?: 0 })
        } catch (e: Exception) {
            emptyList()
        }
    }

    fun completeCheckIn(reservationId: String, roomId: String) {
        viewModelScope.launch {
            _isProcessing.value = true
            try {
                // 1. Odanın durumunu OCCUPIED yap
                roomRepository.updateRoomStatus(roomId, "OCCUPIED")
                
                // 2. Rezervasyona odayı ata ve status'ü CHECKED_IN yap
                reservationRepository.updateReservationStatusAndRoom(reservationId, "CHECKED_IN", roomId)
                
                // Yeniden yükle
                loadTodayCheckIns()
            } catch (e: Exception) {
                e.printStackTrace()
            } finally {
                _isProcessing.value = false
            }
        }
    }
}

// UI State Model
data class CheckInItemData(
    val id: String,
    val guestName: String,
    val firstName: String = "",
    val lastName: String = "",
    val identityNo: String = "",
    val birthDate: String = "",
    val rooms: List<String>,
    val dateRange: String,
    val checkInDate: String = "",
    val checkOutDate: String = "",
    val statusText: String,
    val isCheckInComplete: Boolean,
    val roomType: String
)

